import { getMockCatalog } from '@/modules/search/utils/mock-catalog'
import type { NormalizedProduct, SupplierId } from '@/modules/search/types'
import { storageService } from '@/services/storage'
import { comparisonManager } from './ComparisonManager'
import type {
  ComparisonFiltersState,
  ComparisonPreferences,
  ComparisonPriceHistoryEntry,
  SupplierChannel,
} from './ComparisonTypes'
import { DEFAULT_COMPARISON_PREFERENCES } from './ComparisonTypes'
import type { RawSupplierOffer } from './ComparisonManager'

const PREFS_KEY = 'masterhub.unified-comparison.preferences.v1'

const DISTRIBUTORS: SupplierId[] = ['pauta', 'oderco', 'agis', 'allnations', 'ingram', 'snd']
const WEB: SupplierId[] = ['kabum', 'terabyte', 'pichau', 'amazon', 'mercadolivre', 'magazineluiza']

/**
 * ComparisonService — loads Search catalog + Paraguay mocks into the manager.
 * Does not modify Search Engine or Product Intelligence internals.
 */
export class ComparisonService {
  loadFromSearchCatalog(term = ''): ReturnType<typeof comparisonManager.ingest> {
    comparisonManager.reset()
    const catalog = getMockCatalog()
    const filtered = term.trim()
      ? catalog.filter((p) =>
          `${p.description} ${p.brand} ${p.model} ${p.sku}`.toLowerCase().includes(term.toLowerCase()),
        )
      : catalog

    const raw = filtered.slice(0, 220).map((p) => toRawOffer(p))
    const paraguay = buildParaguayOffers(filtered.slice(0, 40))
    return comparisonManager.ingest([...raw, ...paraguay])
  }

  loadDemoCategories(): ReturnType<typeof comparisonManager.ingest> {
    comparisonManager.reset()
    const demos: RawSupplierOffer[] = [
      ...multiSupplier('SSD Kingston NV3 1TB NVMe PCIe 4.0', 'ssd-nv3', [
        { supplierId: 'agis', supplierName: 'AGIS', price: 369, stock: 52, deliveryDays: 2, channel: 'distributor' },
        { supplierId: 'pauta', supplierName: 'PAUTA', price: 372, stock: 18, deliveryDays: 1, channel: 'distributor' },
        { supplierId: 'allnations', supplierName: 'ALL NATIONS', price: 381, stock: 143, deliveryDays: 3, channel: 'distributor' },
        { supplierId: 'kabum', supplierName: 'KABUM', price: 389, stock: 40, deliveryDays: 4, channel: 'web' },
        { supplierId: 'compras_paraguai', supplierName: 'COMPRAS PARAGUAI', price: 69, stock: 99, deliveryDays: 7, channel: 'paraguay', currency: 'USD' },
      ]),
      ...multiSupplier('Notebook Dell Latitude 5450 Intel i5 16GB SSD 512', 'nb-lat', [
        { supplierId: 'pauta', supplierName: 'PAUTA', price: 4890, stock: 7, deliveryDays: 2, channel: 'distributor' },
        { supplierId: 'oderco', supplierName: 'ODERCO', price: 4920, stock: 4, deliveryDays: 3, channel: 'distributor' },
        { supplierId: 'terabyte', supplierName: 'TERABYTE', price: 5099, stock: 12, deliveryDays: 5, channel: 'web' },
      ]),
      ...categoryPack(),
    ]
    return comparisonManager.ingest(demos)
  }

  setFilters(filters: Partial<ComparisonFiltersState>) {
    return comparisonManager.setFilters(filters)
  }

  setPreferences(preferences: Partial<ComparisonPreferences>) {
    const saved = this.savePreferences({
      ...this.getPreferences(),
      ...preferences,
    })
    return comparisonManager.setPreferences(saved)
  }

  getPreferences(): ComparisonPreferences {
    if (typeof window === 'undefined') return { ...DEFAULT_COMPARISON_PREFERENCES }
    return {
      ...DEFAULT_COMPARISON_PREFERENCES,
      ...storageService.getJSON<Partial<ComparisonPreferences>>(PREFS_KEY, {}),
    }
  }

  savePreferences(preferences: ComparisonPreferences): ComparisonPreferences {
    if (typeof window !== 'undefined') {
      storageService.setJSON(PREFS_KEY, preferences)
    }
    return preferences
  }

  listHistory(fingerprint?: string): ComparisonPriceHistoryEntry[] {
    return comparisonManager.listPriceHistory(fingerprint)
  }
}

function toRawOffer(product: NormalizedProduct): RawSupplierOffer {
  return {
    id: product.id,
    description: product.description,
    supplierId: product.supplier,
    supplierName: product.supplier.toUpperCase(),
    channel: channelOf(product.supplier),
    price: product.pixPrice,
    currency: 'BRL',
    stock: product.stock,
    deliveryDays: product.estimatedDelivery,
    updatedAt: product.lastUpdate,
    sku: product.sku,
    purchasePrice: product.purchasePrice,
  }
}

function channelOf(supplier: SupplierId): SupplierChannel {
  if (DISTRIBUTORS.includes(supplier)) return 'distributor'
  if (WEB.includes(supplier)) return 'web'
  return 'web'
}

function buildParaguayOffers(products: NormalizedProduct[]): RawSupplierOffer[] {
  return products.map((product, index) => ({
    id: `py-${product.id}`,
    description: product.description,
    supplierId: 'compras_paraguai',
    supplierName: 'COMPRAS PARAGUAI',
    channel: 'paraguay' as const,
    price: round2(Math.max(15, product.pixPrice / 5.4)),
    currency: 'USD' as const,
    stock: 20 + (index % 50),
    deliveryDays: 6 + (index % 5),
    updatedAt: product.lastUpdate,
    sku: product.sku,
  }))
}

function multiSupplier(
  description: string,
  key: string,
  offers: Array<{
    supplierId: string
    supplierName: string
    price: number
    stock: number
    deliveryDays: number
    channel: SupplierChannel
    currency?: 'BRL' | 'USD'
  }>,
): RawSupplierOffer[] {
  return offers.map((offer, index) => ({
    id: `${key}-${offer.supplierId}-${index}`,
    description,
    ...offer,
    currency: offer.currency ?? 'BRL',
    updatedAt: new Date().toISOString(),
  }))
}

function categoryPack(): RawSupplierOffer[] {
  const items: Array<{ description: string; base: number }> = [
    { description: 'Servidor Dell PowerEdge R760', base: 28900 },
    { description: 'Monitor Dell 27 Polegadas', base: 1299 },
    { description: 'Impressora HP LaserJet Pro', base: 1899 },
    { description: 'Firewall Mikrotik CCR2004', base: 4200 },
    { description: 'Switch TP-Link 24 portas Gigabit', base: 980 },
    { description: 'Memória Kingston Fury DDR5 32GB', base: 620 },
    { description: 'Fonte Corsair 750W 80 Plus', base: 740 },
    { description: 'Gabinete Cooler Master Mid Tower', base: 390 },
    { description: 'GPU GeForce RTX 5060 8GB', base: 2499 },
    { description: 'CPU AMD Ryzen 7 9700X', base: 2199 },
  ]

  return items.flatMap((item, index) =>
    multiSupplier(item.description, `cat-${index}`, [
      {
        supplierId: 'agis',
        supplierName: 'AGIS',
        price: item.base,
        stock: 10 + index,
        deliveryDays: 2,
        channel: 'distributor',
      },
      {
        supplierId: 'pauta',
        supplierName: 'PAUTA',
        price: round2(item.base * 1.03),
        stock: 5 + index,
        deliveryDays: 1,
        channel: 'distributor',
      },
      {
        supplierId: 'kabum',
        supplierName: 'KABUM',
        price: round2(item.base * 1.08),
        stock: 20,
        deliveryDays: 4,
        channel: 'web',
      },
    ]),
  )
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const comparisonService = new ComparisonService()
